import { useEffect, useState, type RefObject } from 'react';

/**
 * The phone's keyboard, as the Morning chat box needs to know of it (his iPhone findings, 2026-10-28).
 *
 * `up` = he is TYPING in the box: its text input has the focus AND the keyboard is open. iOS and Android leave
 * `innerHeight` alone and shrink the visual viewport, so "open" = the visible area is at least KEYBOARD_MIN shorter than
 * the window. A desktop never shrinks it, so there `up` stays false and nothing changes.
 *
 * `strip` = where to draw a white strip under the pinned box while he types, in the panel's own coordinates. Measured on
 * the iOS 26 simulator: the keyboard's ✓ bar and Safari's address pill FLOAT over the page, see-through, and the pinned
 * box's layer is clipped at the visible area's bottom edge - so whatever list sat further down showed through between the
 * box and the keyboard (a white shadow on the box itself was clipped away with it). Ordinary page content is NOT clipped
 * there, so the strip is page content: absolutely placed in the panel, right under where the box is stuck.
 * ⛔ It never reaches past the panel's own end, so it can never lengthen the page.
 *
 * And when the keyboard he typed with CLOSES, the page is nudged one point and back. Measured on the same simulator: after
 * the keyboard went down the pinned top bar was still DRAWN, but its tap area was stale - a tap on its Assistant landed on
 * a card heading underneath it (so the button "did nothing"). One point of scroll and back makes iOS place it again; the
 * next tap hit the button. Both scrolls run in one go, so nothing is painted between them. (MOB5's family.)
 */
export const KEYBOARD_MIN = 80;
/** Taller than the ✓ bar plus the address pill (about 85 points on an iPhone 17 Pro). */
export const STRIP_HEIGHT = 160;
/**
 * The strip starts this far UP, tucked under the box (which is drawn above it): measured, where iOS clips the box and
 * where the box's own bottom edge reports itself differ by a few points, and a sliver of the list showed between them.
 */
export const STRIP_TUCK = 16;

export interface KeyboardUp {
  up: boolean;
  strip: { top: number; height: number } | null;
}

const DOWN: KeyboardUp = { up: false, strip: null };

const same = (a: KeyboardUp, b: KeyboardUp): boolean =>
  a.up === b.up && a.strip?.top === b.strip?.top && a.strip?.height === b.strip?.height;

/** A text box, not the voice picker or the file input: those are not "typing". */
const isTextInput = (el: Element | null): boolean => {
  if (!el || el.tagName !== 'INPUT') return false;
  const type = (el as HTMLInputElement).type;
  return type === 'text' || type === 'search' || type === '';
};

export function useKeyboardUp(opts: {
  enabled: boolean;
  bar: RefObject<HTMLElement | null>;
  panel: RefObject<HTMLElement | null>;
}): KeyboardUp {
  const { enabled, bar, panel } = opts;
  const [state, setState] = useState<KeyboardUp>(DOWN);

  useEffect(() => {
    if (!enabled) { setState(DOWN); return; }
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    // No visual viewport (an old browser, a test without one): never "up", the box stays as it is.
    if (!vv) return;

    let pending = false;
    let frame = 0;
    // The keyboard was open for the box: its closing is what leaves the top bar's tap area stale.
    let openForBox = false;
    const nudge = () => {
      const x = window.scrollX;
      const y = window.scrollY;
      window.scrollTo(x, y > 0 ? y - 1 : y + 1);
      window.scrollTo(x, y);
    };
    const measure = () => {
      const active = document.activeElement;
      const typing = !!bar.current && bar.current.contains(active) && isTextInput(active);
      const open = window.innerHeight - vv.height >= KEYBOARD_MIN;
      if (typing && open) openForBox = true;
      else if (!open && openForBox) { openForBox = false; nudge(); }
      let next: KeyboardUp = DOWN;
      if (typing && open) {
        const p = panel.current?.getBoundingClientRect();
        const b = bar.current?.getBoundingClientRect();
        let strip: KeyboardUp['strip'] = null;
        if (p && b) {
          const under = Math.round(b.bottom - p.top); // the box's bottom edge, in the panel's coordinates
          const room = Math.round(p.height) - under; // the panel left below it - never more, never past its end
          if (room > 0) strip = { top: under - STRIP_TUCK, height: Math.min(STRIP_HEIGHT, room) + STRIP_TUCK };
        }
        next = { up: true, strip };
      }
      setState((cur) => (same(cur, next) ? cur : next));
    };
    // One measure per frame, however many events arrive (a scroll fires many).
    const schedule = () => {
      if (pending) return;
      pending = true;
      frame = window.requestAnimationFrame(() => { pending = false; measure(); });
    };

    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    window.addEventListener('scroll', schedule, { passive: true });
    document.addEventListener('focusin', schedule);
    document.addEventListener('focusout', schedule);
    // The box changes height when its rows fold away for typing: measure again then too.
    const resized = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    if (resized && bar.current) resized.observe(bar.current);
    schedule();
    return () => {
      if (pending) window.cancelAnimationFrame(frame);
      resized?.disconnect();
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      window.removeEventListener('scroll', schedule);
      document.removeEventListener('focusin', schedule);
      document.removeEventListener('focusout', schedule);
    };
  }, [enabled, bar, panel]);

  return state;
}
